import { useCallback, useEffect, useState } from 'react';
import type { AdminWhatsappSendLogRow } from '../../../domain/superadmin/types';
import { useAppContext } from '../../../contexts/AppContext';
import { fetchAdminWhatsappSendLog } from '../../../services/modules/superadmin/superadminApi';
import { SuperadminApiError } from '../../../services/modules/superadmin/errors';
import { notify } from '../../../utils/toast';

const DEFAULT_LIMIT = 50;

export function useAdminWhatsappSendLog() {
  const [items, setItems] = useState<AdminWhatsappSendLogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [limit] = useState(DEFAULT_LIMIT);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { setCurrentPage } = useAppContext();

  const handleForbidden = useCallback(() => {
    setCurrentPage('dashboard');
    notify.warning('Acesso não autorizado à área de superadmin.');
  }, [setCurrentPage]);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchAdminWhatsappSendLog({ limit, offset });
      setItems(result.items);
      setTotal(result.total);
    } catch (e) {
      if (e instanceof SuperadminApiError && e.isForbidden) {
        handleForbidden();
        return;
      }
      setError(e instanceof Error ? e.message : 'Erro ao carregar log de envios');
    } finally {
      setIsLoading(false);
    }
  }, [limit, offset, handleForbidden]);

  useEffect(() => {
    void load();
  }, [load]);

  const nextPage = useCallback(() => {
    setOffset((cur) => cur + limit);
  }, [limit]);

  const prevPage = useCallback(() => {
    setOffset((cur) => Math.max(0, cur - limit));
  }, [limit]);

  const hasNext = offset + limit < total;
  const hasPrev = offset > 0;

  return {
    items,
    total,
    limit,
    offset,
    isLoading,
    error,
    refetch: load,
    nextPage,
    prevPage,
    hasNext,
    hasPrev,
  };
}
